import React from "react";
import locale from "locale";
import moment from "moment";
import { connect } from "react-redux";
import { useParams, useHistory } from "react-router-dom";
import { LinkContainer } from "react-router-bootstrap";
import parse from "html-react-parser";
import {
    Breadcrumb,
    Col,
    Row,
    ButtonToolbar,
    Button,
    FormGroup,
    ControlLabel,
    FormControl,
    Radio,
    Checkbox,
} from "react-bootstrap";

import {
    requestGetReminder,
    requestUpdateReminder,
    requestGetRecipientParams,
    requestCreateRecipientParams,
    requestGetStreams,
} from "../../../../api/cp";
import DatetimeRangePicker from "../../DatetimeRangePicker";
import LetterTemplatePreview from "../../UI/HtmlPreview";
import ReminderRecipientsModal from "./components/ReminderRecipientsModal";
import useEmailTemplate from "./hooks/useEmailTemplate";
import {
    MAILING_METHODS,
    TIME_MAILING_METHOD,
    PERIODIC_MAILING_METHOD,
    MAIL_SUBJECT_PLACEHOLDERS,
    MAILING_TYPES,
    MANUAl_MAILING_METHOD,
    RECIPIENT_TYPES,
    PARTICIPATION_FORMATS,
    ADDITIONAL_CRITERION_MEASHURES,
} from "./constants";
import {ucFirst} from "../../../../admin/js/lib";

const ReminderEdit = ({ event }) => {
    const BACK_LINK = `/event/${event.eventPassword}/edit-reminders`;

    const { templateId, reminderId } = useParams();
    const history = useHistory();

    const [reminderDetails, setReminderDetails] = React.useState(null);
    const [isRecipientsModalVisible, setIsRecipientsModalVisible] =
        React.useState(false);
    const [mailingMethod, setMailingMethod] = React.useState(null);
    const [recipientType, setRecipientType] = React.useState(null);
    const [participationFormat, setParticipationFormat] = React.useState(
        PARTICIPATION_FORMATS[0].value
    );
    const [useAdditionalParams, setUseAdditionalParams] = React.useState(false);
    const [streamsData, setStreamsData] = React.useState(null);
    const [viewingCriteriaParameters, updateViewingCriteriaParameters] =
        React.useReducer(
            (prev, next) => {
                return { ...prev, ...next };
            },
            {
                viewingDurationUnit: null,
                viewingDurationMin: "",
                viewingDurationMax: "",
                streamId: null,
                episodeId: null,
            }
        );

    const { previewTemplate, setPreviewTemplate, handlePreview } =
        useEmailTemplate();

    React.useEffect(() => {
        getReminderDetails();
    }, [templateId]);

    React.useEffect(() => {
        handleAdditionalParams();
    }, [useAdditionalParams]);

    const isSystemMailing = () =>
        reminderDetails?.recipientsSource === MAILING_TYPES.SYSTEM;

    const isStatisticMailing = () =>
        reminderDetails?.recipientsSource === MAILING_TYPES.STATISTIC;

    function isMailingMethod(method) {
        return mailingMethod === method;
    }

    async function getReminderDetails() {
        try {
            const data = await requestGetReminder(templateId);
            const reminder = data.find(({ id }) => id === parseInt(reminderId));

            setMailingTimeParams(reminder);
            reminder && setReminderDetails(reminder);

            if (reminder?.recipientsSource === MAILING_TYPES.SYSTEM) {
                await handleGetSystemRecepientParams(reminder);
            }

            if (reminder?.recipientsSource === MAILING_TYPES.STATISTIC) {
                await handleGetStatisticRecepientParams(reminder);
                await handleGetStreamsData();
            }
        } catch (error) {
            alert(
                error?.error?.message ??
                    error?.message ??
                    "Error getReminderDetails"
            );
        }
    }

    function setMailingTimeParams(reminder) {
        if (reminder.sendPeriodic === 0) {
            return handleMailingMethodChange(TIME_MAILING_METHOD);
        }

        if (reminder.sendPeriodic === 1) {
            return handleMailingMethodChange(PERIODIC_MAILING_METHOD);
        }

        handleMailingMethodChange(MANUAl_MAILING_METHOD);
    }

    function hadleReminderChange(key, value) {
        setReminderDetails((prev) => {
            return {
                ...prev,
                [key]: value,
            };
        });
    }

    async function handleSave() {
        try {
            const errorMessage = validatePayload(reminderDetails);

            if (errorMessage) {
                alert(errorMessage);

                return;
            }

            isSystemMailing() &&
                (await updateSystemRecipientParticipationParams());
            isStatisticMailing() &&
                (await updateStatisticRecipientParticipationParams());
            await requestUpdateReminder(reminderId, reminderDetails);
            history.replace(BACK_LINK);
        } catch (error) {
            alert(
                error?.error?.message ?? error?.message ?? "Error handleSave"
            );
        }
    }

    function validatePayload(payload) {
        if (!(payload.subjectRu?.trim() && payload.subjectEn?.trim())) {
            return locale("reminders.setMailSubject");
        }

        return null;
    }

    function handleMailingMethodChange(value) {
        setMailingMethod(value);

        if (!reminderDetails) {
            return;
        }

        hadleReminderChange("sendAt", null);
        hadleReminderChange("sendPeriodic", null);
        hadleReminderChange("periodicSendStartDate", null);
        hadleReminderChange("periodicSendEndDate", null);
    }

    function handleChangeMailingTime(sendAt, sendPeriodic) {
        hadleReminderChange("sendAt", sendAt);
        hadleReminderChange("sendPeriodic", sendPeriodic);
    }

    function handleChangeMailingPeriodicTime(type, date) {
        hadleReminderChange(type, date);
        reminderDetails.sendPeriodic !== 1 &&
            hadleReminderChange("sendPeriodic", 1);
    }

    async function handleGetSystemRecepientParams(reminder) {
        try {
            const response = await requestGetRecipientParams(reminder.id);

            if (response?.roleParameters?.role) {
                setRecipientType(response.roleParameters.role);
            }

            if (response?.participationTypeParameters?.participationType) {
                setParticipationFormat(
                    response.participationTypeParameters.participationType
                );
            }
        } catch (error) {
            alert(
                error?.error?.message ??
                    error?.message ??
                    "Error handleGetSystemRecepientParams"
            );
        }
    }

    async function handleGetStreamsData() {
        try {
            const response = await requestGetStreams(event.eventPassword);
            response && response?.length && setStreamsData(response);
        } catch (error) {
            alert(
                error?.error?.message ??
                    error?.message ??
                    "Error handleGetStreamsData"
            );
        }
    }

    function getStreamEpisodesByStreamId(streamId) {
        return streamsData?.find(({ id }) => id === streamId)?.episode;
    }

    async function handleGetStatisticRecepientParams(reminder) {
        try {
            const response = await requestGetRecipientParams(reminder.id);

            if (!response?.viewingCriteriaParameters) {
                return;
            }

            const {
                viewingDurationUnit,
                viewingDurationMin,
                viewingDurationMax,
                streamId,
                episodeId,
            } = response.viewingCriteriaParameters;

            viewingDurationUnit &&
                updateViewingCriteriaParameters({
                    viewingDurationUnit,
                });
            viewingDurationMin &&
                updateViewingCriteriaParameters({
                    viewingDurationMin,
                });
            viewingDurationMax &&
                updateViewingCriteriaParameters({
                    viewingDurationMax,
                });
            streamId &&
                updateViewingCriteriaParameters({
                    streamId,
                });
            episodeId &&
                updateViewingCriteriaParameters({
                    episodeId,
                });

            setUseAdditionalParams(true);
        } catch (error) {
            alert(
                error?.error?.message ??
                    error?.message ??
                    "Error handleGetStatisticRecepientParams"
            );
        }
    }

    async function updateSystemRecipientParticipationParams() {
        const payload = {
            roleParameters: {
                role: recipientType,
            },
            participationTypeParameters: {
                participationType:
                    participationFormat === "null" ? null : participationFormat,
            },
        };

        await requestCreateRecipientParams(reminderDetails.id, payload);
    }

    async function updateStatisticRecipientParticipationParams() {
        const payload = {
            viewingCriteriaParameters: null,
        };

        [
            "viewingDurationUnit",
            "viewingDurationMin",
            "viewingDurationMax",
            "streamId",
            "episodeId",
        ].forEach((criteria) => {
            if (viewingCriteriaParameters[criteria]) {
                if (!payload.viewingCriteriaParameters) {
                    payload.viewingCriteriaParameters = {};
                }

                payload.viewingCriteriaParameters[criteria] =
                    viewingCriteriaParameters[criteria];
            }
        });

        await requestCreateRecipientParams(reminderDetails.id, payload);
    }

    function handleAdditionalParams() {
        if (!isStatisticMailing() || useAdditionalParams) {
            return;
        }

        updateViewingCriteriaParameters({
            viewingDurationUnit: null,
            viewingDurationMin: "",
            viewingDurationMax: "",
            streamId: null,
            episodeId: null,
        });
    }

    function getPeriodicSendDate(sendType) {
        if (!reminderDetails[sendType]) {
            return "";
        }

        if (typeof reminderDetails[sendType] === "string") {
            return reminderDetails[sendType];
        }

        return moment
            .unix(reminderDetails[sendType])
            .format("YYYY-MM-DD HH:mm");
    }

    function renderAdditionalMailingContent() {
        if (isSystemMailing()) {
            return (
                <>
                    <Row>
                        <Col xs={2}>
                            <FormGroup>
                                <ControlLabel>
                                    {locale("reminders.recipients")}
                                </ControlLabel>
                                {RECIPIENT_TYPES.map(
                                    ({
                                        label,
                                        value,
                                        disableDuringPreModeration,
                                    }) => {
                                        if (
                                            disableDuringPreModeration &&
                                            !reminderDetails
                                                ?.emailMessageTemplate
                                                .accessPreModeration
                                        ) {
                                            return "";
                                        }

                                        return (
                                            <Radio
                                                key={value}
                                                value={value}
                                                name="RECIPIENT_TYPES"
                                                checked={
                                                    value === recipientType
                                                }
                                                onChange={({ target }) =>
                                                    setRecipientType(
                                                        target.value
                                                    )
                                                }
                                            >
                                                {label}
                                            </Radio>
                                        );
                                    }
                                )}
                            </FormGroup>
                        </Col>
                        <Col xs={2}>
                            <FormGroup>
                                <ControlLabel>
                                    {locale("reminders.participationFormat")}
                                </ControlLabel>
                                <FormControl
                                    componentClass="select"
                                    value={participationFormat}
                                    onChange={({ target }) =>
                                        setParticipationFormat(target.value)
                                    }
                                >
                                    {PARTICIPATION_FORMATS.map(
                                        ({ label, value }) => (
                                            <option key={value} value={value}>
                                                {label}
                                            </option>
                                        )
                                    )}
                                </FormControl>
                            </FormGroup>
                        </Col>
                    </Row>
                </>
            );
        }

        if (isStatisticMailing()) {
            return (
                <>
                    <Row>
                        <Col xs={4}>
                            <Checkbox
                                value={useAdditionalParams}
                                checked={useAdditionalParams}
                                onChange={() =>
                                    setUseAdditionalParams(!useAdditionalParams)
                                }
                            >
                                {locale("reminders.useAdditionalCriterion")}
                            </Checkbox>
                        </Col>
                    </Row>
                    {useAdditionalParams && (
                        <>
                            <Row>
                                <Col xs={4}>
                                    <FormGroup>
                                        <p>
                                            {parse(
                                                locale(
                                                    "reminders.additionalCriterionCondition"
                                                )
                                            )}
                                        </p>
                                        <FormGroup>
                                            <ControlLabel>
                                                {locale(
                                                    "reminders.additionalCriterionMeashure"
                                                )}
                                            </ControlLabel>
                                            {ADDITIONAL_CRITERION_MEASHURES.map(
                                                ({ label, value }) => (
                                                    <Radio
                                                        key={value}
                                                        value={value}
                                                        name="ADDITIONAL_CRITERION_MEASHURE"
                                                        checked={
                                                            value ===
                                                            viewingCriteriaParameters.viewingDurationUnit
                                                        }
                                                        onChange={({
                                                            target,
                                                        }) =>
                                                            updateViewingCriteriaParameters(
                                                                {
                                                                    viewingDurationUnit:
                                                                        target.value,
                                                                }
                                                            )
                                                        }
                                                    >
                                                        {label}
                                                    </Radio>
                                                )
                                            )}
                                        </FormGroup>
                                        <FormGroup>
                                            <ControlLabel>
                                                {locale("reminders.set")} X
                                            </ControlLabel>
                                            <FormControl
                                                type="number"
                                                value={
                                                    viewingCriteriaParameters.viewingDurationMin
                                                }
                                                onChange={({ target }) =>
                                                    updateViewingCriteriaParameters(
                                                        {
                                                            viewingDurationMin:
                                                                target.value,
                                                        }
                                                    )
                                                }
                                            />
                                        </FormGroup>
                                        <FormGroup>
                                            <ControlLabel>
                                                {locale("reminders.set")} Y (
                                                {locale("reminders.optional")})
                                            </ControlLabel>
                                            <FormControl
                                                type="number"
                                                value={
                                                    viewingCriteriaParameters.viewingDurationMax
                                                }
                                                onChange={({ target }) =>
                                                    updateViewingCriteriaParameters(
                                                        {
                                                            viewingDurationMax:
                                                                target.value,
                                                        }
                                                    )
                                                }
                                            />
                                        </FormGroup>
                                        <FormGroup>
                                            <ControlLabel>
                                                {locale(
                                                    "reminders.setViewingType"
                                                )}
                                            </ControlLabel>
                                            <FormControl
                                                componentClass="select"
                                                value={
                                                    viewingCriteriaParameters.streamId ||
                                                    ""
                                                }
                                                onChange={({ target }) =>
                                                    updateViewingCriteriaParameters(
                                                        {
                                                            streamId: parseInt(
                                                                target.value
                                                            ),
                                                        }
                                                    )
                                                }
                                            >
                                                <option value={null}>
                                                    Все залы
                                                </option>
                                                {streamsData?.length &&
                                                    streamsData.map(
                                                        (stream) => (
                                                            <option
                                                                key={
                                                                    stream.name
                                                                }
                                                                value={
                                                                    stream.id
                                                                }
                                                            >
                                                                {
                                                                    stream.description_ru
                                                                }
                                                            </option>
                                                        )
                                                    )}
                                            </FormControl>
                                            <FormControl
                                                style={{ marginTop: "15px" }}
                                                componentClass="select"
                                                value={
                                                    viewingCriteriaParameters.episodeId ||
                                                    ""
                                                }
                                                disabled={
                                                    !viewingCriteriaParameters?.streamId
                                                }
                                                onChange={({ target }) =>
                                                    updateViewingCriteriaParameters(
                                                        {
                                                            episodeId: parseInt(
                                                                target.value
                                                            ),
                                                        }
                                                    )
                                                }
                                            >
                                                <option value={null}>
                                                    Все эпизоды
                                                </option>
                                                {getStreamEpisodesByStreamId(
                                                    viewingCriteriaParameters?.streamId
                                                )?.map((episode) => (
                                                    <option
                                                        key={episode.key}
                                                        value={episode.id}
                                                    >
                                                        {episode.name_ru}
                                                    </option>
                                                ))}
                                            </FormControl>
                                        </FormGroup>
                                    </FormGroup>
                                </Col>
                            </Row>
                        </>
                    )}
                </>
            );
        }

        return (
            <Row>
                <Col style={{ marginTop: "15px" }} xs={12}>
                    <Button onClick={() => setIsRecipientsModalVisible(true)}>
                        {locale("reminders.addRecipients")}
                    </Button>
                </Col>
            </Row>
        );
    }

    const placeholders = [
        ...MAIL_SUBJECT_PLACEHOLDERS,
        'userName',
        event.registrationFields.map(({field}) => 'user' + ucFirst(field.name)),
    ]

    return (
        <section>
            <Row>
                <Col>
                    <Breadcrumb>
                        <LinkContainer to={`/event/${event.eventPassword}`}>
                            <Breadcrumb.Item>
                                {locale("reminders.broadcast")}
                            </Breadcrumb.Item>
                        </LinkContainer>
                        <LinkContainer to={BACK_LINK}>
                            <Breadcrumb.Item>
                                {locale("reminders.mailing")}
                            </Breadcrumb.Item>
                        </LinkContainer>
                        <Breadcrumb.Item active>
                            {locale("reminders.editing")}
                        </Breadcrumb.Item>
                    </Breadcrumb>
                </Col>
            </Row>
            {reminderDetails && (
                <form>
                    <Row style={{ marginTop: "15px" }}>
                        <Col xs={6}>
                            <FormGroup>
                                <ControlLabel>
                                    {locale("reminders.mailSubject")} (Ru)
                                </ControlLabel>
                                <FormControl
                                    type="text"
                                    value={reminderDetails.subjectRu || ""}
                                    onChange={({ target }) =>
                                        hadleReminderChange(
                                            "subjectRu",
                                            target.value
                                        )
                                    }
                                />
                            </FormGroup>
                        </Col>
                        <Col xs={6}>
                            <FormGroup>
                                <ControlLabel>
                                    {locale("reminders.mailSubject")} (En)
                                </ControlLabel>
                                <FormControl
                                    type="text"
                                    value={reminderDetails.subjectEn || ""}
                                    onChange={({ target }) =>
                                        hadleReminderChange(
                                            "subjectEn",
                                            target.value
                                        )
                                    }
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <p>
                        <i>
                            {locale("reminders.availableValues")}:{" "}
                            {placeholders.map((name) => `${name}`).join(", ")}
                        </i>
                    </p>
                    <Row>
                        <Col xs={4}>
                            <FormGroup>
                                <ControlLabel>
                                    {locale("reminders.mailingType")}:{" "}
                                    {isSystemMailing()
                                        ? locale("reminders.autoMailingType")
                                        : locale("reminders.manualMailingType")}
                                </ControlLabel>
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        {mailingMethod && (
                            <Col xs={4}>
                                <FormGroup>
                                    <ControlLabel>
                                        {locale("reminders.mailingMethod")}
                                    </ControlLabel>
                                    <FormControl
                                        componentClass="select"
                                        value={mailingMethod}
                                        onChange={({ target }) =>
                                            handleMailingMethodChange(
                                                target.value
                                            )
                                        }
                                    >
                                        {MAILING_METHODS.map(
                                            ({ value, label }) => (
                                                <option
                                                    key={value}
                                                    value={value}
                                                >
                                                    {label}
                                                </option>
                                            )
                                        )}
                                    </FormControl>
                                </FormGroup>
                            </Col>
                        )}
                        {isMailingMethod(TIME_MAILING_METHOD) && (
                            <Col xs={4}>
                                <FormGroup>
                                    <ControlLabel>
                                        {locale("reminders.mailingDatetime")}
                                    </ControlLabel>
                                    <DatetimeRangePicker
                                        singleDatePicker
                                        timePicker
                                        timePicker24Hour
                                        showDropdowns
                                        onApply={(_, picker) =>
                                            handleChangeMailingTime(
                                                picker.startDate.format(
                                                    "YYYY-MM-DD HH:mm"
                                                ),
                                                0
                                            )
                                        }
                                    >
                                        <FormControl
                                            type="text"
                                            defaultValue={
                                                reminderDetails.sendAt || ""
                                            }
                                        />
                                    </DatetimeRangePicker>
                                </FormGroup>
                            </Col>
                        )}
                        {isMailingMethod(PERIODIC_MAILING_METHOD) && (
                            <>
                                <Col xs={2}>
                                    <FormGroup>
                                        <ControlLabel>
                                            {locale(
                                                "reminders.periodicSendStartDate"
                                            )}
                                        </ControlLabel>
                                        <DatetimeRangePicker
                                            singleDatePicker
                                            timePicker
                                            timePicker24Hour
                                            showDropdowns
                                            onApply={(_, picker) =>
                                                handleChangeMailingPeriodicTime(
                                                    "periodicSendStartDate",
                                                    picker.startDate.format(
                                                        "YYYY-MM-DD HH:mm"
                                                    )
                                                )
                                            }
                                        >
                                            <FormControl
                                                key={`periodicSendStartDate${reminderDetails.periodicSendStartDate}`}
                                                type="text"
                                                defaultValue={getPeriodicSendDate(
                                                    "periodicSendStartDate"
                                                )}
                                            />
                                        </DatetimeRangePicker>
                                    </FormGroup>
                                </Col>
                                <Col xs={2}>
                                    <FormGroup>
                                        <ControlLabel>
                                            {locale(
                                                "reminders.periodicSendEndDate"
                                            )}
                                        </ControlLabel>
                                        <DatetimeRangePicker
                                            singleDatePicker
                                            timePicker
                                            timePicker24Hour
                                            showDropdowns
                                            onApply={(_, picker) =>
                                                handleChangeMailingPeriodicTime(
                                                    "periodicSendEndDate",
                                                    picker.startDate.format(
                                                        "YYYY-MM-DD HH:mm"
                                                    )
                                                )
                                            }
                                        >
                                            <FormControl
                                                key={`periodicSendEndDate${reminderDetails.periodicSendEndDate}`}
                                                type="text"
                                                defaultValue={getPeriodicSendDate(
                                                    "periodicSendEndDate"
                                                )}
                                            />
                                        </DatetimeRangePicker>
                                    </FormGroup>
                                </Col>
                                <Col xs={2}>
                                    <FormGroup>
                                        <ControlLabel>
                                            {locale("reminders.mailingTime")}
                                        </ControlLabel>
                                        <FormGroup
                                            style={{
                                                display: "flex",
                                                gap: "8px",
                                                alignItems: "center",
                                            }}
                                        >
                                            <input
                                                type="time"
                                                value={
                                                    reminderDetails.sendAt ?? ""
                                                }
                                                onChange={({ target }) =>
                                                    handleChangeMailingTime(
                                                        target.value,
                                                        1
                                                    )
                                                }
                                            />
                                        </FormGroup>
                                    </FormGroup>
                                </Col>
                            </>
                        )}
                    </Row>
                    {renderAdditionalMailingContent()}
                </form>
            )}
            <Row style={{ marginTop: "96px" }}>
                <Col xs={12}>
                    <ButtonToolbar>
                        <Button
                            bsStyle="info"
                            onClick={() => handlePreview(templateId)}
                        >
                            {locale("reminders.watch")}
                        </Button>
                        <Button bsStyle="success" onClick={handleSave}>
                            {locale("reminders.save")}
                        </Button>
                        <LinkContainer to={BACK_LINK}>
                            <Button>{locale("reminders.back")}</Button>
                        </LinkContainer>
                    </ButtonToolbar>
                </Col>
            </Row>
            <LetterTemplatePreview
                isModalVisible={Boolean(previewTemplate)}
                template={previewTemplate}
                onHide={() => setPreviewTemplate(null)}
            />
            {reminderDetails && (
                <ReminderRecipientsModal
                    isRecipientsModalVisible={isRecipientsModalVisible}
                    reminder={reminderDetails}
                    onClose={() => setIsRecipientsModalVisible(false)}
                />
            )}
        </section>
    );
};

const mapStateToProps = (store) => ({
    event: store.currentEvent,
});

export default connect(mapStateToProps)(ReminderEdit);
